/**
 * WCC360 v2 — the ported v1 instrument, on screen.
 *
 * Two block families, both derived from the item bank so there is no second
 * list to keep in sync:
 *   · Trait blocks  — 5-point statements, six to a screen (`LikertBlock`).
 *   · Quiz blocks   — Interests / Aptitudes / Values, four to a screen.
 *
 * Answers accumulate into two store keys (`v1_trait_answers`,
 * `v1_quiz_answers`) rather than one key per screen, because the scorer reads
 * the instrument as a whole and a learner may revisit a block. Everything they
 * produce reaches the engine only through the capped nudge in `v1port/nudge.ts`.
 */
import { useAssessmentV2 } from "../store";
import { LikertBlock } from "../components/interactions/LikertBlock";
import {
  ChoiceBlock, type ChoiceBlockQuestion,
} from "../components/interactions/ChoiceBlock";
import { LIKERT_OPTIONS } from "../v1port/traitBank";
import {
  QUIZ_ANSWERS_KEY, TRAIT_ANSWERS_KEY, TRAIT_BLOCKS, ageGroupFor,
  quizBlockFor, quizById, traitBlockFor,
} from "../v1port/blocks";
import type { QuizQuestion } from "../v1port/nibble";

function Head({ eyebrow, title, subtitle }: { eyebrow?: string; title: string; subtitle?: string }) {
  return (
    <div className="mb-8">
      {eyebrow && (
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">{eyebrow}</div>
      )}
      <h1 className="font-display text-3xl md:text-4xl font-medium leading-tight">{title}</h1>
      {subtitle && <p className="mt-3 text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

/**
 * Read and merge one of the two instrument answer maps.
 *
 * The map is an aggregate (see `PART2_DERIVED_KEYS` in the store), so merging
 * into it does not create an evidence row. `commit` writes the block's own row
 * under its screen id, which is what a replay walks.
 */
function useInstrumentAnswers(key: string, screenId: string) {
  const { state, setPart2 } = useAssessmentV2();
  const answers = (state.part2[key] as Record<string, number> | undefined) ?? {};
  const merge = (patch: Record<string, number>) => setPart2(key, { ...answers, ...patch });
  const commit = (patch: Record<string, number>) => {
    merge(patch);
    setPart2(screenId, patch);
  };
  return [answers, merge, commit] as const;
}

// -----------------------------------------------------------------------
// Trait blocks
// -----------------------------------------------------------------------

export function TraitBlockScreen({
  screenId, onNext,
}: { screenId: string; onNext: () => void }) {
  const { state } = useAssessmentV2();
  const [answers, merge, commit] = useInstrumentAnswers(TRAIT_ANSWERS_KEY, screenId);
  const block = traitBlockFor(screenId);
  if (!block) return null;

  const age = ageGroupFor(state.band);
  const items = block.items.map((q) => ({ id: q.id, prompt: q.variants[age] }));
  const mine = Object.fromEntries(
    block.items
      .filter((q) => answers[String(q.id)] !== undefined)
      .map((q) => [String(q.id), answers[String(q.id)]]),
  ) as Record<string, number>;

  return (
    <div className="max-w-2xl mx-auto">
      <Head
        eyebrow={`How you see yourself · ${block.index + 1} of ${TRAIT_BLOCKS.length}`}
        title="How true does each of these feel?"
        subtitle="Answer for how you actually are, not how you'd like to be. Nobody sees these but you."
      />
      <LikertBlock
        items={items}
        scale={LIKERT_OPTIONS}
        answers={Object.keys(mine).length ? mine : undefined}
        onChange={merge}
        onComplete={(a) => { commit(a); setTimeout(onNext, 200); }}
      />
    </div>
  );
}

// -----------------------------------------------------------------------
// Quiz blocks — Interests · Aptitudes · Values
// -----------------------------------------------------------------------

function toChoiceQuestion(q: QuizQuestion): ChoiceBlockQuestion {
  return {
    id: q.id,
    prompt: q.prompt,
    options: q.options.map((o) => ({ label: o.label, emoji: o.emoji })),
    compact: q.kind === "likert",
  };
}

export function QuizBlockScreen({
  screenId, onNext,
}: { screenId: string; onNext: () => void }) {
  const [answers, merge, commit] = useInstrumentAnswers(QUIZ_ANSWERS_KEY, screenId);
  const block = quizBlockFor(screenId);
  if (!block) return null;

  const quiz = quizById(block.quizId);
  const total = Math.ceil(quiz.questions.length / block.items.length) || 1;
  const questions = block.items.map(toChoiceQuestion);
  const mine = Object.fromEntries(
    block.items
      .filter((q) => answers[q.id] !== undefined)
      .map((q) => [q.id, answers[q.id]]),
  ) as Record<string, number>;

  return (
    <div className="max-w-2xl mx-auto">
      <Head
        eyebrow={`${block.emoji} ${block.quizTitle} · ${block.index + 1} of ${total}`}
        title={block.first ? quiz.title : "A few more"}
        subtitle={block.first ? quiz.intro : "Same idea — pick what feels most like you."}
      />
      <ChoiceBlock
        questions={questions}
        answers={Object.keys(mine).length ? mine : undefined}
        onChange={merge}
        onComplete={(a) => { commit(a); setTimeout(onNext, 200); }}
      />
    </div>
  );
}
